import _merge from 'lodash/merge';
import { Lightning, Router } from '@lightningjs/sdk';
import { featureFlipping } from '@bedrock-rdk/config';
import { getInitialStyle } from './ads.style';
import { Overlay } from '../interface/overlay/overlay.component';
import { t } from '../../i18n';
import connect from './ads.connector';
import { Metadata } from '../interface/metadata/metadata.component';
import { PlaybackStateIndicator } from './playbackStateIndicator/playbackStateIndicator.component';
import {
  PLAYBACK_STATE_ENDED,
  PLAYBACK_STATE_PAUSED,
  PLAYBACK_STATE_PLAYING,
  TOP_BAR_BACK,
} from '../../../logic/player/player.constants';
import { DISPLAY_AD_METADATA, PAUSE_SIGNAL, PLAY_SIGNAL, SKIP_SIGNAL, TOP_BAR_ITEMS } from './ads.constants';
import { CallToAction } from '../../tornado/callToAction/primary/callToAction.component';
import { TopBar } from '../interface/topBar/topBar.component';
import { Pill } from '../interface/pill/pill.component';
import { SELECTED_SIGNAL } from '../../tornado/signals';
import { adExitAnalyticsEvent, updateCountdown } from './ads.utils';
import { handleAdPlayerScale } from './platform/default/lBanner.utils';

export class Ads extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Overlay: {
          type: Overlay,
        },
        TopBar: {
          type: TopBar,
          items: [
            {
              id: TOP_BAR_ITEMS.BACK,
              icon: 'ArrowLeftWithBase',
              text: t('nav.backToNav'),
              action: TOP_BAR_BACK,
            },
          ],
          signals: {
            [SELECTED_SIGNAL]: '_handleTopBarItemSelected',
          },
        },
        Metadata: {
          type: Metadata,
          title: t('player.adMessage'),
          extraTitle: t('player.adExtraTitle'),
        },
        PlaybackStateIndicator: {
          type: PlaybackStateIndicator,
        },
        Countdown: {
          type: Pill,
          title: t('player.adMessage'),
        },
        Skip: {
          type: CallToAction,
          label: t('player.adMessage'),
          picto: {
            type: 'icon',
            name: 'SkipNext',
          },
        },
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Playing extends this {
        $enter() {
          this.tag('PlaybackStateIndicator').show('Paused');
          this._updateSkipAdButtonState();
          this._dropFocus();
        }

        _handleEnter() {
          if (this._handleSkipButtonPress()) {
            return true;
          }

          this._pause();

          return true;
        }

        _handlePause() {
          this._pause();

          return true;
        }

        _handlePlayPause() {
          this._pause();

          return true;
        }
      },
      class Paused extends this {
        $enter() {
          this.tag('PlaybackStateIndicator').show('Playing');
        }

        _handleEnter() {
          if (this._handleSkipButtonPress()) {
            return true;
          }

          this._play();

          return true;
        }

        _handlePlay() {
          this._play();

          return true;
        }

        _handlePlayPause() {
          this._play();

          return true;
        }
      },
    ];
  }

  _construct() {
    this._shouldDisplayAdJingle = false;
    this._displayedAdBreakType = null;
    this._skippableAdsProgressTime = undefined;
    this._canSkipAd = false;
    this._canSkipAdBreak = false;
    this._skipButtonPressLocked = false;
    this._displayAdsCountdown = false;
    this._isAdvertisementMode = false;
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this._focused = undefined;
  }

  _active() {
    this._updateSkipAdButtonState();
  }

  set advertisement(value) {
    if (!value || !value.brParams || !featureFlipping.player_skippableAd) {
      this._skippableAdsProgressTime = undefined;
      this._canSkipAd = false;
      this._canSkipAdBreak = false;

      return;
    }

    this._canSkipAd = Boolean(value.brParams.skippableAdsActivation);
    this._canSkipAdBreak = Boolean(value.brParams.skippableAdsSkipAll);
    this._skippableAdsProgressTime = Math.round((value.brParams.skippableAdsProgressTime || 0) / 1000);
    this._refocus();
  }

  set adSkipOffset(value) {
    this._adSkipOffset = value;
    this._updateSkipAdButtonState();
  }

  set adElapsedTime(value) {
    this._adElapsedTime = value;
    this._updateSkipAdButtonState();
  }

  set contentTitle(value) {
    this.tag('Metadata').extraTitle = t('player.adExtraTitle', {
      contentTitle: value || '',
    });
  }

  set shouldDisplayLBanner(value) {
    handleAdPlayerScale(value);
  }

  set playbackState(value) {
    if (value === PLAYBACK_STATE_PLAYING) {
      this._setState('Playing');
    } else if (value === PLAYBACK_STATE_PAUSED) {
      this._setState('Paused');
    } else if (value === PLAYBACK_STATE_ENDED) {
      this._dropFocus();
      this.tag('Skip').visible = false;
    }
  }

  set displayedAdBreakType(value) {
    this._displayedAdBreakType = value;
  }

  set adBreakElapsedTime(value) {
    this._adBreakElapsedTime = value;
    // normally elapsed time should increase, otherwise it means the countdown was reset, no need to animate
    this._updateCountdown(value < this._adBreakElapsedTime);
  }

  set adBreakDuration(value) {
    this._adBreakDuration = value;
    this._updateCountdown();
  }

  set displayAdsCountdown(value) {
    this._displayAdsCountdown = value;
    this._updateCountdownVisibility();
  }

  set isAdvertisementMode(value) {
    this._isAdvertisementMode = value;
    this._updateCountdownVisibility();
  }

  // The countdown pill is shown only while a real ad is on screen - never during the
  // break-start gap, the before/after jingle images, or the jingle clips that share
  // this ads surface. `adBreakDuration` still drives the pill's alpha; this gates `visible`.
  _updateCountdownVisibility() {
    this.tag('Countdown').visible = Boolean(this._displayAdsCountdown) && this._isAdvertisementMode;
  }

  _updateCountdown(skipAnimation = false) {
    const view = this.tag('Countdown');
    view.alpha = this._adBreakDuration ? 1 : 0;
    updateCountdown(view, this._adBreakElapsedTime, this._adBreakDuration, skipAnimation);
  }

  _dropFocus() {
    if (this._focused !== this.tag('Skip')) {
      this._focused = undefined;
    }
  }

  _handleSkipButtonPress() {
    if (this._skipButtonPressLocked) {
      return false;
    }

    if (this._focused === this.tag('Skip')) {
      this._skipButtonPressLocked = true;
      this.signal(SKIP_SIGNAL, this._canSkipAdBreak);

      return true;
    }

    return false;
  }

  _updateSkipAdButtonState() {
    const button = this.tag('Skip');
    this._skipButtonPressLocked = false;

    if (
      !(this._canSkipAd || this._canSkipAdBreak) ||
      !Number.isFinite(this._skippableAdsProgressTime) ||
      !Number.isFinite(this._adElapsedTime) ||
      !Number.isFinite(this._adSkipOffset)
    ) {
      button.visible = false;
      this._focused = undefined;
      this._refocus();

      return;
    }

    button.visible = (this._canSkipAdBreak && button.visible) || this._adElapsedTime >= this._skippableAdsProgressTime;

    if (!button.visible) {
      if (!this._canSkipAdBreak && this._focused === button) {
        this._focused = undefined;
        this._refocus();
      }

      return;
    }

    if (this._focused === button) {
      return;
    }

    if (this._adElapsedTime >= this._adSkipOffset) {
      button.label = t('player.skipAd');
      button.progress = undefined;
      this._focused = button;
      this._refocus();
    } else {
      const countdown = Math.round(this._adSkipOffset - this._adElapsedTime);
      button.label = `${t('player.skipIn')} ${countdown}`;
      if (!Number.isFinite(button.progress)) {
        button.progress = this._adElapsedTime / this._adSkipOffset;
      }

      const currentProgress = this._progress;
      this._progress = Math.min(1, this._adElapsedTime / this._adSkipOffset);
      if (Number.isFinite(currentProgress) && this._progress > currentProgress) {
        button.setSmooth('progress', this._progress, {
          duration: 0.3,
          timingFunction: 'linear',
        });
      } else {
        button.progress = this._progress;
      }
    }
  }

  _pause() {
    this.signal(PAUSE_SIGNAL);
  }

  _play() {
    this.signal(PLAY_SIGNAL);
  }

  _handleBack() {
    adExitAnalyticsEvent(this._displayedAdBreakType);

    return false;
  }

  _handleUp() {
    if (!DISPLAY_AD_METADATA) {
      return false;
    }

    if (!this._focused) {
      this._focused = this.tag('TopBar');

      return true;
    }

    return false;
  }

  _handleDown() {
    if (!DISPLAY_AD_METADATA) {
      return false;
    }

    if (this._focused) {
      this._focused = undefined;

      return true;
    }

    return false;
  }

  _handleLeft() {
    return true;
  }

  _handleRight() {
    return true;
  }

  _getFocused() {
    return this._focused;
  }

  _handleTopBarItemSelected() {
    Router.back();
  }
}
